import { useState } from 'react';
import { TrendingUp, TrendingDown, Shield, ChevronDown, ChevronUp, Check, X, MessageSquare, Undo2 } from 'lucide-react';

const DIRECTION_CONFIG = {
  buy:  { icon: TrendingUp, color: 'text-korinth-accent', bg: 'bg-korinth-accent/10', border: 'border-l-korinth-accent', label: 'BUY' },
  sell: { icon: TrendingDown, color: 'text-korinth-danger', bg: 'bg-korinth-danger/10', border: 'border-l-korinth-danger', label: 'SELL' },
  hold: { icon: Shield, color: 'text-korinth-warning', bg: 'bg-korinth-warning/10', border: 'border-l-korinth-warning', label: 'HOLD' },
  hedge:{ icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-l-blue-400', label: 'HEDGE' },
};

function ConvictionBar({ label, value, max = 10 }) {
  const pct = Math.min(100, (value / max) * 100);
  const color = value >= 7 ? '#10b981' : value >= 5 ? '#f59e0b' : '#6b7280';
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[9px] font-mono text-korinth-muted w-8">{label}</span>
      <div className="flex-1 h-1.5 bg-korinth-bg rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-[9px] font-mono text-korinth-text w-5 text-right">{value?.toFixed?.(1) || '—'}</span>
    </div>
  );
}

const CONSENSUS_COLORS = { bullish: '#10b981', bearish: '#ef4444', neutral: '#6b7280', mixed: '#f59e0b' };

const SM_COLORS = { accumulating: '#10b981', distributing: '#ef4444', neutral: '#6b7280' };

export default function RecommendationCard({ rec, onAskAbout, consensus, smartMoney }) {
  const [expanded, setExpanded] = useState(false);
  const [acted, setActed] = useState(false);

  const dir = DIRECTION_CONFIG[rec.direction] || DIRECTION_CONFIG.hold;
  const DirIcon = dir.icon;
  const convLabel = rec.conviction_overall >= 7.5 ? 'HIGH' : rec.conviction_overall >= 5 ? 'MEDIUM' : 'LOW';
  const convColor = rec.conviction_overall >= 7.5 ? 'text-korinth-accent' : rec.conviction_overall >= 5 ? 'text-korinth-warning' : 'text-korinth-muted';

  const reasoning = typeof rec.reasoning === 'string' ? rec.reasoning : typeof rec.reasoning === 'object' && rec.reasoning !== null ? (rec.reasoning.summary || rec.reasoning.reasoning || JSON.stringify(rec.reasoning)) : '';
  const exitTriggers = Array.isArray(rec.exit_triggers) ? rec.exit_triggers : [];
  const tickers = Array.isArray(rec.tickers) ? rec.tickers : [];
  const entryStrategy = typeof rec.entry_strategy === 'string' ? rec.entry_strategy : rec.entry_strategy?.strategy || rec.entry_strategy || '';

  async function handleAction(action) {
    try {
      await fetch(`/api/kairos/recommendations/${rec.id}/${action}`, { method: 'POST' });
      setActed(action);
    } catch { /* silent */ }
  }

  return (
    <div className={`bg-korinth-surface border border-korinth-border rounded-lg border-l-4 ${dir.border}`}>
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <DirIcon className={`w-6 h-6 ${dir.color}`} />
            <div>
              <h3 className="text-lg font-mono font-bold text-white">{rec.asset}</h3>
              <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${dir.bg} ${dir.color}`}>{dir.label}</span>
            </div>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-mono font-bold ${convColor}`}>{rec.conviction_overall?.toFixed(1)}<span className="text-sm text-korinth-muted">/10</span></div>
            <div className={`text-[10px] font-mono font-bold ${convColor}`}>{convLabel}</div>
          </div>
        </div>

        {/* Multi-timeframe bars */}
        <div className="space-y-1 mb-3">
          <ConvictionBar label="Tact" value={rec.conviction_tactical} />
          <ConvictionBar label="Swing" value={rec.conviction_swing} />
          <ConvictionBar label="Strat" value={rec.conviction_strategic} />
        </div>

        {/* Reasoning summary */}
        <p className="text-xs text-korinth-text leading-relaxed mb-3">{reasoning.slice(0, 200)}{reasoning.length > 200 ? '...' : ''}</p>

        {/* Position + Entry */}
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          {rec.position_size_recommended_pct && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-korinth-bg text-korinth-text border border-korinth-border/50">
              Size: {rec.position_size_recommended_pct}% <span className="text-korinth-muted">(max {rec.position_size_max_pct}%)</span>
            </span>
          )}
          {entryStrategy && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-500/10 text-blue-400">
              {String(entryStrategy).replace(/_/g, ' ')}
            </span>
          )}
          {tickers.map(t => (
            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-korinth-border text-korinth-text">{t}</span>
          ))}
          {smartMoney && smartMoney.direction !== 'neutral' && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border" style={{
              color: SM_COLORS[smartMoney.direction] || '#6b7280',
              borderColor: (SM_COLORS[smartMoney.direction] || '#6b7280') + '40',
              backgroundColor: (SM_COLORS[smartMoney.direction] || '#6b7280') + '10',
            }}>
              SM: {smartMoney.direction} ({smartMoney.strength}/100)
              {((smartMoney.direction === 'accumulating' && rec.direction === 'sell') || (smartMoney.direction === 'distributing' && rec.direction === 'buy')) && <span className="ml-1 text-korinth-warning">&#9888; Disagrees</span>}
            </span>
          )}
          {consensus && (
            <span className="text-[10px] font-mono px-2 py-0.5 rounded border" style={{
              color: CONSENSUS_COLORS[consensus.consensus_stance] || '#6b7280',
              borderColor: (CONSENSUS_COLORS[consensus.consensus_stance] || '#6b7280') + '40',
              backgroundColor: (CONSENSUS_COLORS[consensus.consensus_stance] || '#6b7280') + '10',
            }}>
              Street: {consensus.consensus_stance} ({consensus.bullish_count}/{consensus.bearish_count})
              {consensus.divergence_from_kairos === 'contrary' && <span className="ml-1 text-korinth-warning">&#9889; Contrarian</span>}
            </span>
          )}
        </div>

        {/* Expand/collapse */}
        <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1 text-[10px] font-mono text-korinth-muted hover:text-korinth-accent">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? 'Hide details' : 'Why? See full reasoning'}
        </button>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-korinth-border/30 space-y-2">
            <p className="text-xs text-korinth-text leading-relaxed">{reasoning}</p>
            {exitTriggers.length > 0 && (
              <div>
                <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-1">Exit Triggers</h4>
                <ul className="space-y-0.5">
                  {exitTriggers.map((t, i) => (
                    <li key={i} className="text-[10px] font-mono text-korinth-text flex items-start gap-1.5">
                      <span className="text-korinth-danger mt-0.5">×</span>
                      <span>{typeof t === 'string' ? t : JSON.stringify(t)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-korinth-border/30">
          {!acted ? (
            <>
              <button onClick={() => handleAction('acted')} className="flex items-center gap-1 px-3 py-1.5 rounded text-[10px] font-mono font-bold bg-korinth-accent/10 text-korinth-accent border border-korinth-accent/30 hover:bg-korinth-accent/20">
                <Check className="w-3 h-3" /> I Acted
              </button>
              <button onClick={() => handleAction('skipped')} className="flex items-center gap-1 px-3 py-1.5 rounded text-[10px] font-mono font-bold bg-korinth-muted/10 text-korinth-muted border border-korinth-border hover:bg-korinth-muted/20">
                <X className="w-3 h-3" /> Skipped
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono text-korinth-muted">{acted === 'acted' ? '✓ Acted' : '— Skipped'}</span>
              <button onClick={async () => { try { await fetch(`/api/kairos/recommendations/${rec.id}/undo-action`, { method: 'POST' }); setActed(false); } catch {} }}
                className="flex items-center gap-0.5 text-[9px] font-mono text-korinth-muted hover:text-korinth-accent">
                <Undo2 className="w-2.5 h-2.5" /> Undo
              </button>
            </div>
          )}
          <button onClick={() => onAskAbout?.(rec)} className="flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-mono text-korinth-muted hover:text-korinth-accent ml-auto">
            <MessageSquare className="w-3 h-3" /> Ask about this
          </button>
          <span className="text-[8px] font-mono text-korinth-muted/50">{rec.created_at?.slice(5, 16)}</span>
        </div>
      </div>
    </div>
  );
}
